/**
 * Action verification helpers (blink, head turn, smile detection)
 * These analyze landmark sequences to verify challenge actions
 */

export interface ActionVerification {
  action: string;
  detected: boolean;
  confidence: number;
}

/**
 * Detect blink from eye landmark ratios
 */
export function detectBlink(
  landmarks: number[][],
  _frameIndex?: number,
  _history?: number[][][]
): boolean {
  // MediaPipe face mesh eye landmarks (approximate indices)
  // Left eye: ~33-42, Right eye: ~263-272
  // Use eye aspect ratio (EAR) formula

  if (landmarks.length < 468) {
    // Not enough landmarks (MediaPipe face mesh has 468)
    return false;
  }

  // Simplified: check if eye landmarks are close together (closed)
  // In production, use proper EAR calculation
  const leftEyeTop = landmarks[159]?.[1] || 0;
  const leftEyeBottom = landmarks[145]?.[1] || 0;
  const leftEyeLeft = landmarks[33]?.[0] || 0;
  const leftEyeRight = landmarks[133]?.[0] || 0;

  const rightEyeTop = landmarks[386]?.[1] || 0;
  const rightEyeBottom = landmarks[374]?.[1] || 0;
  const rightEyeLeft = landmarks[362]?.[0] || 0;
  const rightEyeRight = landmarks[263]?.[0] || 0;

  const leftEAR =
    Math.abs(leftEyeTop - leftEyeBottom) / (Math.abs(leftEyeLeft - leftEyeRight) + 1e-6);
  const rightEAR =
    Math.abs(rightEyeTop - rightEyeBottom) / (Math.abs(rightEyeLeft - rightEyeRight) + 1e-6);
  const avgEAR = (leftEAR + rightEAR) / 2;

  // Blink threshold: EAR < 0.2 (eye closed)
  return avgEAR < 0.2;
}

/**
 * Detect head turn (yaw) from nose position relative to face center
 */
export function detectHeadTurn(landmarks: number[][], direction: 'left' | 'right'): boolean {
  if (landmarks.length < 468) return false;

  // Nose tip is typically around index 4
  const noseTip = landmarks[4] || landmarks[0];
  if (!noseTip) return false;

  // Face center (average of key face points)
  const facePoints = [
    landmarks[10], // forehead
    landmarks[152], // chin
    landmarks[234], // left cheek
    landmarks[454], // right cheek
  ].filter(Boolean);

  if (facePoints.length === 0) return false;

  const faceCenterX = facePoints.reduce((sum, p) => sum + p[0], 0) / facePoints.length;
  const noseX = noseTip[0];

  const offset = noseX - faceCenterX;
  const threshold = 0.05; // 5% of face width

  if (direction === 'left') {
    return offset < -threshold;
  } else {
    return offset > threshold;
  }
}

/**
 * Detect smile from mouth landmark positions
 */
export function detectSmile(landmarks: number[][]): boolean {
  if (landmarks.length < 468) return false;

  // Mouth corners: ~61, 291
  const leftMouth = landmarks[61];
  const rightMouth = landmarks[291];
  const mouthTop = landmarks[13]; // upper lip
  const mouthBottom = landmarks[14]; // lower lip

  if (!leftMouth || !rightMouth || !mouthTop || !mouthBottom) return false;

  // Mouth opening width
  const mouthWidth = Math.abs(leftMouth[0] - rightMouth[0]);
  const mouthHeight = Math.abs(mouthTop[1] - mouthBottom[1]);

  // Smile: mouth is wider and slightly open
  const aspectRatio = mouthWidth / (mouthHeight + 1e-6);
  return aspectRatio > 2.5 && mouthHeight > 0.01;
}

/**
 * Verify action sequence across frame history
 */
export function verifyActionSequence(
  action: string,
  frameHistory: { landmarks: number[][]; ts_ms: number }[]
): ActionVerification {
  if (frameHistory.length < 2) {
    return { action, detected: false, confidence: 0.0 };
  }

  let detected = false;
  let confidence = 0.0;

  switch (action.toUpperCase()) {
    case 'BLINK':
      // Check for blink in recent frames
      for (let i = 0; i < frameHistory.length; i++) {
        if (detectBlink(frameHistory[i].landmarks, i)) {
          detected = true;
          confidence = 0.8;
          break;
        }
      }
      break;

    case 'TURN_LEFT':
      detected = frameHistory.some((f) => detectHeadTurn(f.landmarks, 'left'));
      confidence = detected ? 0.75 : 0.0;
      break;

    case 'TURN_RIGHT':
      detected = frameHistory.some((f) => detectHeadTurn(f.landmarks, 'right'));
      confidence = detected ? 0.75 : 0.0;
      break;

    case 'SMILE':
      detected = frameHistory.some((f) => detectSmile(f.landmarks));
      confidence = detected ? 0.7 : 0.0;
      break;

    default:
      detected = false;
      confidence = 0.0;
  }

  return { action, detected, confidence };
}
